import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowLeft, RefreshCw, Plus, X, Gift, ShoppingCart, MinusCircle, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import AdminNav from "../components/admin/AdminNav";
import OrderModal from "../components/admin/OrderModal";

const TIER_STYLE = {
  bronze: "bg-[#a97142] text-cream border-[#a97142]",
  silver: "bg-[#9aa0a8] text-cream border-[#9aa0a8]",
  gold: "bg-gold text-burgundy-ink border-gold",
  platinum: "bg-burgundy text-cream border-burgundy",
};

const fmtDate = (s) => { try { return new Date(s).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }); } catch { return s; } };

export default function AdminClients() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [modal, setModal] = useState(null); // {type, amount, checkAmount, note, reason}
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients", { params: search ? { search } : {} });
      setClients(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    setSelectedId(id);
    try {
      const { data } = await api.get(`/admin/clients/${id}`);
      setDetail(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const closeDetail = () => { setSelectedId(null); setDetail(null); setModal(null); };

  const handleAction = async (e) => {
    e.preventDefault();
    if (!detail || !modal) return;
    try {
      let url, body;
      if (modal.type === "purchase") {
        url = `/admin/clients/${detail.client.id}/purchase`;
        body = { amount: parseInt(modal.amount, 10), note: modal.note || undefined };
      } else if (modal.type === "redeem") {
        url = `/admin/clients/${detail.client.id}/redeem`;
        body = { amount: parseInt(modal.amount, 10), check_amount: parseInt(modal.checkAmount, 10), note: modal.note || undefined };
      } else if (modal.type === "gift") {
        url = `/admin/clients/${detail.client.id}/gift`;
        body = { amount: parseInt(modal.amount, 10), reason: modal.reason || "Подарочные бонусы" };
      }
      await api.post(url, body);
      toast.success("Готово");
      setModal(null);
      await loadDetail(detail.client.id);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doDelete = async () => {
    if (!detail) return;
    if (!window.confirm(`Удалить клиента ${detail.client.name}? Отменить нельзя.`)) return;
    try {
      await api.delete(`/admin/clients/${detail.client.id}`);
      toast.success("Клиент удалён");
      closeDetail();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runHolidays = async () => {
    if (!window.confirm("Начислить праздничные / ДР бонусы всем подходящим клиентам за сегодня?")) return;
    try {
      const { data } = await api.post("/admin/clients/holidays/run");
      toast.success(`Начислено подарков: ${data.granted}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createClient = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/clients", {
        name: fd.get("name"), phone: fd.get("phone"),
        birthday: fd.get("birthday") || undefined,
        bonus_balance: parseInt(fd.get("bonus_balance") || "0", 10),
        total_spent: parseInt(fd.get("total_spent") || "0", 10),
      });
      toast.success("Клиент создан");
      setShowCreate(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Unified Luxury Navigation */}
      <AdminNav onOpenNewOrder={() => setIsOrderModalOpen(true)} />

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-8 md:py-10">
        <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl text-ink heading-tight">
              Клиенты & Программа лояльности <span className="text-burgundy/50 text-2xl md:text-3xl ml-2">· {clients.length}</span>
            </h1>
            <p className="mt-2 text-sm text-ink/80">
              Учет бонусных баллов, уровней кэшбека и истории покупок
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runHolidays} className="btn-ghost !py-2.5 !px-4" data-testid="btn-run-holidays">
              <Gift size={14} strokeWidth={2}/> Праздники
            </button>
            <button onClick={() => setShowCreate(true)} className="btn-primary !py-2.5 !px-4" data-testid="btn-new-client">
              <Plus size={14} strokeWidth={2.5}/> Добавить клиента
            </button>
          </div>
        </div>

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted_ink"/>
            <input type="text" placeholder="Поиск по имени или телефону…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-cream-paper border-2 border-burgundy/40 focus:border-burgundy outline-none pl-10 pr-3 py-2.5 text-sm"
              data-testid="clients-search"/>
          </div>
          <button onClick={load} className="p-2.5 border-2 border-burgundy text-burgundy hover:bg-burgundy hover:text-cream transition-colors" title="Обновить"><RefreshCw size={16} strokeWidth={2} className={loading ? "animate-spin" : ""}/></button>
        </div>

        <div className="border-2 border-burgundy bg-cream-paper overflow-x-auto shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-burgundy text-cream">
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Имя</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Телефон</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Тир</th>
                <th className="text-right px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Баланс</th>
                <th className="text-right px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Куплено на</th>
                <th className="text-left px-4 py-3 font-bold uppercase tracking-[0.18em] text-[11px]">Регистрация</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-muted_ink">{loading ? "Загружаем…" : "Клиентов ещё нет"}</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} onClick={() => loadDetail(c.id)}
                    className="border-t border-burgundy/20 hover:bg-cream-light cursor-pointer" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3 font-bold text-ink">{c.name}</td>
                  <td className="px-4 py-3 font-mono text-burgundy">{c.phone}</td>
                  <td className="px-4 py-3"><span className={`px-2.5 py-1 border-2 uppercase tracking-[0.16em] text-[10px] font-bold ${TIER_STYLE[c.tier]}`}>{c.tier} · {c.tier_pct}%</span></td>
                  <td className="px-4 py-3 text-right font-heading text-xl text-burgundy">{c.bonus_balance} ₽</td>
                  <td className="px-4 py-3 text-right text-ink">{c.total_spent.toLocaleString("ru-RU")} ₽</td>
                  <td className="px-4 py-3 text-muted_ink whitespace-nowrap">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Drawer: Client details */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-burgundy-ink/70 flex justify-end" onClick={closeDetail}>
          <div className="w-full max-w-xl bg-cream border-l-2 border-burgundy h-full overflow-y-auto flex flex-col"
               onClick={(e) => e.stopPropagation()} data-testid="client-drawer">
            <div className="p-6 bg-cream-paper border-b-2 border-burgundy">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className={`px-2.5 py-1 border-2 uppercase tracking-[0.16em] text-[10px] font-bold ${TIER_STYLE[detail.client.tier]}`}>
                    {detail.client.tier} · кэшбек {detail.client.tier_pct}%
                  </span>
                  <h2 className="mt-3 font-heading text-4xl text-ink" data-testid="client-detail-name">{detail.client.name}</h2>
                  <p className="font-mono text-burgundy mt-1" data-testid="client-detail-phone">{detail.client.phone}</p>
                </div>
                <button onClick={closeDetail} className="p-2 text-muted_ink hover:text-burgundy" data-testid="close-drawer"><X size={20}/></button>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 p-4 bg-cream border border-burgundy/30">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted_ink">Баланс бонусов</span>
                  <div className="font-heading text-3xl text-burgundy" data-testid="client-detail-balance">{detail.client.bonus_balance} ₽</div>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted_ink">До следующего уровня</span>
                  <div className="text-sm font-bold text-ink mt-1">
                    {detail.next_tier_threshold ? `${(detail.next_tier_threshold - detail.client.total_spent).toLocaleString("ru-RU")} ₽` : "Максимум"}
                  </div>
                  <div className="mt-2 text-sm text-ink">Куплено на <b>{detail.client.total_spent.toLocaleString("ru-RU")} ₽</b></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => setModal({ type: "purchase", amount: "", note: "" })} className="btn-primary !py-3 !px-4" data-testid="btn-purchase"><ShoppingCart size={14} strokeWidth={2.5}/>Покупка</button>
                <button onClick={() => setModal({ type: "redeem", amount: "", checkAmount: "", note: "" })} className="btn-ghost !py-3 !px-4" data-testid="btn-redeem"><MinusCircle size={14} strokeWidth={2.5}/>Списать</button>
                <button onClick={() => setModal({ type: "gift", amount: "500", reason: "Подарок" })} className="btn-ghost !py-3 !px-4" data-testid="btn-gift"><Gift size={14} strokeWidth={2.5}/>Подарок</button>
                <button onClick={doDelete} className="ml-auto p-3 text-muted_ink hover:text-burgundy" title="Удалить клиента"><Trash2 size={14} strokeWidth={2}/></button>
              </div>
            </div>

            <div className="p-5">
              <p className="text-[11px] uppercase tracking-[0.24em] font-bold text-muted_ink mb-3">История транзакций</p>
              {detail.transactions.length === 0 && <p className="text-muted_ink italic">Транзакций пока нет</p>}
              <ul className="space-y-2" data-testid="tx-list">
                {detail.transactions.map((t) => (
                  <li key={t.id} className="border border-burgundy/30 bg-cream-paper p-3 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className={`font-heading text-xl ${t.amount > 0 ? "text-burgundy" : "text-muted_ink"}`}>
                        {t.amount > 0 ? "+" : ""}{t.amount} ₽
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted_ink">{t.type}</span>
                    </div>
                    <p className="mt-1 text-ink/85">{t.reason}</p>
                    <div className="mt-1.5 flex items-center justify-between text-xs text-muted_ink">
                      <span>{fmtDate(t.created_at)}</span>
                      <span>Баланс: {t.balance_after} ₽</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-burgundy-ink/70 flex items-center justify-center px-5" onClick={() => setModal(null)}>
          <form onSubmit={handleAction} onClick={(e) => e.stopPropagation()} className="bg-cream-paper border-2 border-burgundy p-6 md:p-8 w-full max-w-md space-y-4" data-testid="action-modal">
            <h3 className="font-heading text-3xl text-ink">
              {modal.type === "purchase" && "Новая покупка"}
              {modal.type === "redeem" && "Списать бонусы"}
              {modal.type === "gift" && "Подарочные бонусы"}
            </h3>
            {modal.type === "redeem" && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Сумма чека, ₽</label>
                <input type="number" required min="1" value={modal.checkAmount || ""} onChange={(e) => setModal({...modal, checkAmount: e.target.value})} className="input-vintage" data-testid="input-check-amount"/>
                <p className="mt-1 text-xs text-muted_ink">Списать можно не более 30% от чека</p>
              </div>
            )}
            <div>
              <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">
                {modal.type === "purchase" && "Сумма покупки, ₽"}
                {modal.type === "redeem" && "Сколько бонусов списать, ₽"}
                {modal.type === "gift" && "Сумма подарка, ₽"}
              </label>
              <input type="number" required min="1" value={modal.amount || ""} onChange={(e) => setModal({...modal, amount: e.target.value})} className="input-vintage" data-testid="input-amount"/>
            </div>
            {modal.type === "gift" && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Повод</label>
                <input type="text" required value={modal.reason || ""} onChange={(e) => setModal({...modal, reason: e.target.value})} className="input-vintage" data-testid="input-reason"/>
              </div>
            )}
            {(modal.type === "purchase" || modal.type === "redeem") && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Комментарий (необязательно)</label>
                <input type="text" value={modal.note || ""} onChange={(e) => setModal({...modal, note: e.target.value})} className="input-vintage"/>
              </div>
            )}
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1">Отмена</button>
              <button type="submit" className="btn-primary flex-1" data-testid="submit-action">Подтвердить</button>
            </div>
          </form>
        </div>
      )}

      {/* Create client modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-burgundy-ink/70 flex items-center justify-center px-5" onClick={() => setShowCreate(false)}>
          <form onSubmit={createClient} onClick={(e) => e.stopPropagation()} className="bg-cream-paper border-2 border-burgundy p-6 md:p-8 w-full max-w-md space-y-4" data-testid="create-client-form">
            <h3 className="font-heading text-3xl text-ink">Новый клиент</h3>
            <div><label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Имя</label><input name="name" required className="input-vintage" data-testid="new-client-name"/></div>
            <div><label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Телефон</label><input name="phone" required placeholder="+7…" className="input-vintage" data-testid="new-client-phone"/></div>
            <div><label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">День рождения (необязательно)</label><input type="date" name="birthday" className="input-vintage"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Стартовые бонусы</label><input type="number" name="bonus_balance" min="0" defaultValue="0" className="input-vintage"/></div>
              <div><label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-muted_ink mb-2">Уже потратил, ₽</label><input type="number" name="total_spent" min="0" defaultValue="0" className="input-vintage"/></div>
            </div>
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost flex-1">Отмена</button>
              <button type="submit" className="btn-primary flex-1">Создать</button>
            </div>
          </form>
        </div>
      )}

      {/* Order Modal */}
      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        onSuccess={() => load()}
      />

      <Toaster position="top-right" toastOptions={{ style: { background: "#F4E9D2", color: "#1A0E07", border: "2px solid #4A1419", borderRadius: 0 } }} />
    </div>
  );
}
